
import React from 'react';
import { Tag, Checkbox } from 'dingtalk-design-desktop';
import type { FieldInfo } from '../types';
import { MarkdownEditor } from './MarkdownEditor';
import { useFixedTemplate } from '../hooks/useFixedTemplate';
import { useEffect, useState } from 'react';
import { useMessageTemplates } from '../hooks/useMessageTemplates';
import { Modal, Input } from 'dingtalk-design-desktop';

interface MessageEditorProps {
  messageTemplate: string;
  onTemplateChange: (template: string) => void;
  fields: FieldInfo[];
  atAll: boolean;
  onAtAllChange: (atAll: boolean) => void;
  disabled?: boolean;
}

export const MessageEditor: React.FC<MessageEditorProps> = ({
  messageTemplate,
  onTemplateChange,
  fields,
  atAll,
  onAtAllChange,
  disabled = false,
}) => {
  const { fixedTemplate, saveTemplate, deleteTemplate } = useFixedTemplate();
  const isCurrentFixed = fixedTemplate === messageTemplate;

  // 模板列表相关（不再需要传入platform）
  const { templates, addTemplate, fetchTemplates } = useMessageTemplates();
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [newTplName, setNewTplName] = useState('');
  const [saveError, setSaveError] = useState('');
  const [templateListOpen, setTemplateListOpen] = useState(false);
  // 插入字段变量到消息模板
  const insertFieldVariable = (fieldName: string) => {
    const variable = `{${fieldName}}`;
    const newTemplate = messageTemplate + variable;
    onTemplateChange(newTemplate);
  };
  // 选择模板填充内容
  const handleSelectTemplate = (tpl: any) => {
    onTemplateChange(tpl.content || '');
    setTemplateListOpen(false);
  };

  // 保存为固定模板弹窗逻辑
  const handleSaveTemplate = () => {
    setNewTplName('');
    setSaveError('');
    setSaveModalOpen(true);
  };
  const handleConfirmSave = async () => {
    if (!newTplName.trim()) {
      setSaveError('请输入模板名称');
      return;
    }
    if (templates.some(t => t.name === newTplName.trim())) {
      setSaveError('模板名称已存在，请重新命名');
      return;
    }
    try {
      await addTemplate(newTplName.trim(), messageTemplate);
      setSaveModalOpen(false);
      setNewTplName('');
      setSaveError('');
      fetchTemplates();
    } catch (e: any) {
      setSaveError(e.message || '保存失败');
    }
  };

  return (
    <div className="message-editor">
      <label className="editor-label">
        ✍️ 消息内容（支持字段变量和Markdown格式）
        <span style={{ color: '#888', fontSize: '0.98em', marginLeft: 8 }}>
          （两个空格加回车为换行，支持标准Markdown语法）
        </span>
      </label>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <MarkdownEditor value={messageTemplate} onChange={onTemplateChange} disabled={disabled} />
          <div style={{ margin: '8px 0 0 0', display: 'flex', gap: 12 }}>
            <button
              type="button"
              disabled={disabled}
              style={{ padding: '4px 12px', borderRadius: 4, border: '1px solid #1677ff', background: '#fff', color: '#1677ff', cursor: disabled ? 'not-allowed' : 'pointer' }}
              onClick={handleSaveTemplate}
            >
              保存为固定模板
            </button>
                  {/* 保存模板弹窗 */}
                  <Modal
                    title="保存为固定模板"
                    visible={saveModalOpen}
                    onOk={handleConfirmSave}
                    onCancel={() => setSaveModalOpen(false)}
                    okText="保存"
                    cancelText="取消"
                  >
                    <div style={{ marginBottom: 8 }}>请输入模板名称：</div>
                    <Input value={newTplName} onChange={e => setNewTplName(e.target.value)} maxLength={32} placeholder="模板名称" />
                    {saveError && <div style={{ color: '#ff4d4f', marginTop: 6 }}>{saveError}</div>}
                  </Modal>
            {fixedTemplate && (
              <button
                type="button"
                disabled={disabled}
                style={{ padding: '4px 12px', borderRadius: 4, border: '1px solid #ff4d4f', background: '#fff', color: '#ff4d4f', cursor: disabled ? 'not-allowed' : 'pointer' }}
                onClick={deleteTemplate}
              >
                删除固定模板
              </button>
            )}
          </div>
        </div>
        <div style={{ minWidth: 120, maxWidth: 220, marginTop: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          {/* 模板列表下拉菜单 */}
          <div style={{ marginBottom: 12, width: '100%' }}>
            <div style={{ fontWeight: 500, marginBottom: 4 }}>模板列表：</div>
            <div style={{ position: 'relative' }}>
              <button
                type="button"
                style={{ width: '100%', padding: '4px 8px', borderRadius: 4, border: '1px solid #1677ff', background: '#fff', color: '#1677ff', cursor: 'pointer', textAlign: 'left' }}
                onClick={() => setTemplateListOpen((v) => !v)}
              >
                选择模板 ▼
              </button>
              {templateListOpen && (
                <div style={{ position: 'absolute', zIndex: 10, top: 32, left: 0, right: 0, background: '#fff', border: '1px solid #e5e6eb', borderRadius: 6, boxShadow: '0 2px 8px #0002', maxHeight: 220, overflowY: 'auto' }}>
                  {templates.length === 0 && <div style={{ color: '#bbb', padding: 12 }}>暂无模板</div>}
                  {templates.map((tpl) => (
                    <div
                      key={tpl.id}
                      style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f0f0f0', fontSize: 15, color: '#222' }}
                      onClick={() => handleSelectTemplate(tpl)}
                    >
                      {tpl.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="variables-label" style={{ marginBottom: 4, fontWeight: 500 }}>可用字段：</div>
          <div className="variables-list" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {fields.map((field) => (
              <Tag
                key={field.id}
                onClick={() => !disabled && insertFieldVariable(field.name)}
                style={{
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  margin: 0,
                  marginBottom: 4,
                  width: 'fit-content',
                  fontSize: '0.98rem',
                }}
              >
                + {field.name}
              </Tag>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};